import clsx, { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { AIMessage, HumanMessage, SystemMessage } from '@langchain/core/messages';
import { Document } from 'langchain/document';
import { CHAINS } from './constants';
import dayjs from 'dayjs';
import { formatNumberWithBrackets } from './utils/formatNumberWithBrackets';

export const cn = (...classes: ClassValue[]) => twMerge(clsx(...classes));

export const formatTimeDifference = (date1: Date | string, date2: Date | string): string => {
  date1 = new Date(date1);
  date2 = new Date(date2);

  const diffInSeconds = Math.floor(Math.abs(date2.getTime() - date1.getTime()) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''}`;
  else if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minute${Math.floor(diffInSeconds / 60) !== 1 ? 's' : ''}`;
  else if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) !== 1 ? 's' : ''}`;
  else if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) !== 1 ? 's' : ''}`;
  else
    return `${Math.floor(diffInSeconds / 31536000)} year${Math.floor(diffInSeconds / 31536000) !== 1 ? 's' : ''}`;
};

export const docByScoreToSystemMessage = ([doc, score]: [Document, number]) => {
  return new SystemMessage(doc.pageContent);
};
export const docToSystemMessage = (doc: Document) => {
  return new SystemMessage(doc.pageContent);
};
export function docToMessage(doc: Document) {
  const role = doc.metadata.role || 'user'; // 默认用户消息
  switch (role.toLowerCase()) {
    case 'system':
      return new SystemMessage(doc.pageContent);
    case 'assistant':
      return new AIMessage(doc.pageContent);
    default:
      return new HumanMessage(doc.pageContent);
  }
}

export const formatAddress = (address: string, before = 5, after = 4) => {
  if (address) {
    const start = address.substring(0, before);
    const end = address.substring(address.length - after, address.length);
    if (address.length < 10) {
      return address;
    }
    return `${start}...${end}`;
  }
  return address;
};

export function timeAgo(time?: string | Date, isLong?: boolean, isAgo?: boolean): string {
  if (!time) return '';
  const now = new Date();
  const inputTime = new Date(time);
  const diffInSeconds = Math.floor((now.getTime() - inputTime.getTime()) / 1000); //

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(diffInSeconds / 3600);
  const days = Math.floor(diffInSeconds / 86400);

  if (minutes < 60) {
    if (minutes > 0) {
      return `${minutes}${isLong ? `min` : 'm'} ${isAgo ? 'ago' : ''}`;
    } else {
      return `<${1}${isLong ? `min` : 'm'} ${isAgo ? 'ago' : ''}`;
      // return `${diffInSeconds}${isLong ? `sec` : "s"} ${isAgo ? "ago" : ""}`
    }
  } else if (hours < 24) {
    return `${hours}${isLong ? `hour` : 'h'} ${isAgo ? 'ago' : ''}`;
  } else {
    return `${days}${isLong ? `day` : 'd'} ${isAgo ? 'ago' : ''}`;
  }
}

export const getChain = (chainId?: number) => {
  if (!chainId) return null;
  return CHAINS?.find((chain) => chain.chainId === chainId);
};

export function formatNumber(value: number | string): string {
  if (typeof value === 'string') {
    value = parseFloat(value);
  }

  return value?.toLocaleString('en-US');
}

export function formatToPercentage(value: number, accuracy: number = 2): string {
  let percentage = (value * Math.pow(10, 2)).toFixed(accuracy);

  if (percentage.endsWith(`.${new Array(accuracy).fill(0).join('')}`)) {
    return `${parseInt(percentage)}%`;
  }

  return `${percentage}%`;
}

export function roundDownToNearest5Minutes(timestamp: string | number) {
  // 使用 dayjs 解析时间戳
  let date = dayjs(timestamp);

  // 获取当前分钟数
  const minutes = date.minute();

  // 向前取整到最近的 5 分钟
  const roundedMinutes = Math.floor(minutes / 5) * 5;

  // 设置分钟数，并将秒和毫秒归零
  date = date.minute(roundedMinutes).second(0).millisecond(0);

  // 格式化输出
  return date.format('MM/DD hh:mm A');
}

export function isTimeExceed24Hours(time: string | Date) {
  const now = new Date();
  const inputTime = new Date(time);
  const diffInSeconds = Math.floor((now.getTime() - inputTime.getTime()) / 1000); //

  return diffInSeconds > 24 * 60 * 60 * 1000;
}

export function toFixedNumber(value: number, fixed: number) {
  return value?.toFixed(fixed)?.replace(/\.?0+$/, '');
}

export function formatNumberToUnit(number?: number): string {
  if (number && isNaN(number)) return '';
  if (!number) return number?.toString() || '';
  if (number >= 1e18) {
    return (number / 1e18).toFixed(2)?.replace(/\.?0+$/, '') + 'E'; // 百亿亿（Exillion）
  } else if (number >= 1e15) {
    return (number / 1e15).toFixed(2)?.replace(/\.?0+$/, '') + 'P'; // 千万亿（Quadrillion）
  } else if (number >= 1e12) {
    return (number / 1e12).toFixed(2)?.replace(/\.?0+$/, '') + 'T'; // 万亿
  } else if (number >= 1e9) {
    return (number / 1e9).toFixed(2)?.replace(/\.?0+$/, '') + 'B'; // (Billion)
  } else if (number >= 1e6) {
    return (number / 1e6).toFixed(2)?.replace(/\.?0+$/, '') + 'M'; // (Million)
  } else if (number >= 1e3) {
    return (number / 1e3).toFixed(2)?.replace(/\.?0+$/, '') + 'K'; // (Thousand)
  } else {
    // return formatNumberWithSubscriptZero(number, 4)
    return formatNumberWithBrackets(number, {
      decimal: 4,
    });
    // return number
    //   .toFixed(2)
    //   ?.replace(/\.?0+$/, "")
    //   .toString() //
  }
}

export function formatAbsNumberToUnit(number?: number): string {
  if (number && isNaN(number)) return '';
  if (!number) return number?.toString() || '';
  if (Math.abs(number) >= 1e18) {
    return (number / 1e18).toFixed(2)?.replace(/\.?0+$/, '') + 'E'; // 百亿亿（Exillion）
  } else if (Math.abs(number) >= 1e15) {
    return (number / 1e15).toFixed(2)?.replace(/\.?0+$/, '') + 'P'; // 千万亿（Quadrillion）
  } else if (Math.abs(number) >= 1e12) {
    return (number / 1e12).toFixed(2)?.replace(/\.?0+$/, '') + 'T'; // 万亿
  } else if (Math.abs(number) >= 1e9) {
    return (number / 1e9).toFixed(2)?.replace(/\.?0+$/, '') + 'B'; // (Billion)
  } else if (Math.abs(number) >= 1e6) {
    return (number / 1e6).toFixed(2)?.replace(/\.?0+$/, '') + 'M'; // (Million)
  } else if (Math.abs(number) >= 1e3) {
    return (number / 1e3).toFixed(2)?.replace(/\.?0+$/, '') + 'K'; // (Thousand)
  } else {
    return number
      .toFixed(2)
      ?.replace(/\.?0+$/, '')
      .toString(); //
  }
}

export function formatAmount(
  amount: number,
  decimal: number = 2,
  prefix?: string,
  subfix?: string,
  showBracket?: boolean,
): React.ReactNode {
  if (!amount) {
    return <div>{Number(0).toFixed(decimal)}</div>;
  }
  if (amount >= 1000000000000) {
    return (
      <div>
        {prefix}
        {(amount / 1000000000000).toFixed(decimal)}T{subfix && `${subfix}`}
      </div>
    );
  } else if (amount >= 1000000000) {
    return (
      <div>
        {prefix}
        {(amount / 1000000000).toFixed(decimal)}B{subfix && `${subfix}`}
      </div>
    );
  } else if (amount >= 1000000) {
    return (
      <div>
        {prefix}
        {(amount / 1000000).toFixed(decimal)}M{subfix && `${subfix}`}
      </div>
    );
  } else if (amount >= 1000) {
    return (
      <div>
        {prefix}
        {(amount / 1000).toFixed(decimal)}K{subfix && `${subfix}`}
      </div>
    );
  } else {
    if (showBracket) {
      return (
        <div>
          {prefix}
          {formatNumberWithBrackets(+amount)}
          {subfix && `${subfix}`}
        </div>
      );
    }
    return (
      <div>
        {prefix}
        {(+amount).toFixed(decimal)}
        {subfix && `${subfix}`}
      </div>
    );
  }
}

export function numberFormatter(num?: number, options?: Intl.NumberFormatOptions): string {
  if (num === undefined || num === null || isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }).format(num);
}

export function cryptoPriceFormatter(price?: number): string {
  if (price === undefined || price === null || isNaN(price)) return '';
  if (price >= 1) {
    return numberFormatter(price, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else if (price >= 0.01 && price < 1) {
    return numberFormatter(price, {
      // minimumFractionDigits: 4,
      maximumFractionDigits: 4,
      trailingZeroDisplay: 'stripIfInteger',
    });
  } else if (price < 0.01 && price >= 0.000001) {
    return numberFormatter(price, {
      // minimumFractionDigits: 6,
      maximumFractionDigits: 6,
      trailingZeroDisplay: 'stripIfInteger',
    });
  } else {
    const result = numberFormatter(price, {
      // minimumFractionDigits: 6,
      maximumFractionDigits: 6,
      trailingZeroDisplay: 'stripIfInteger',
    });
    const decimalStr = result.split('.')[1] || '';
    let leadingZeros = 0;
    for (const c of decimalStr) {
      if (c === '0') leadingZeros++;
      else break;
    }
    if (leadingZeros >= 4) {
      const significantDigits = decimalStr.slice(leadingZeros, leadingZeros + 4).replace(/0+$/, '');
      return `0.0${toSup(leadingZeros - 1)}${significantDigits}`;
    }
    return result;
  }
}

function toSub(n: number) {
  const m = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
  return String(n)
    .split('')
    .map((c) => m[+c])
    .join('');
}

function toSup(n: number) {
  const m = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
  return String(n)
    .split('')
    .map((c) => m[+c])
    .join('');
}

export const currencyFormatter = (number: number, options?: Intl.NumberFormatOptions) => {
  const notation = options?.notation ?? 'compact';
  const compactDecimalOptions =
    notation === 'compact'
      ? {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }
      : {};

  return numberFormatter(number, {
    style: 'currency',
    currency: 'USD',
    notation,
    trailingZeroDisplay: 'stripIfInteger',
    ...compactDecimalOptions,
    ...options,
  });
};
